/*
 * `POST /api/presence/heartbeat` every two minutes while the tab is visible.
 * The server treats a user whose last beat is older than five minutes as
 * "away", which is what routes mention notifications to email.
 */
import { useEffect, type FC } from "react";

import { PresenceAPI } from "./api/notificationsApi";

export const HEARTBEAT_MS = 2 * 60_000;

export const usePresenceHeartbeat = (enabled = true): void => {
  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const beat = () => {
      if (document.visibilityState !== "visible") return;
      void PresenceAPI.heartbeat().catch(() => {});
    };
    const start = () => {
      if (timer) return;
      beat();
      timer = setInterval(beat, HEARTBEAT_MS);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const sync = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", sync);
    };
  }, [enabled]);
};

export const PresenceHeartbeat: FC<{ enabled?: boolean }> = ({ enabled }) => {
  usePresenceHeartbeat(enabled);
  return null;
};
